import { v4 as uuidv4 } from 'uuid';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { useEffectOnce } from 'react-use';
import { connect } from 'react-redux';
import { useCallback } from 'react';

import { useForceUpdate } from 'CorePackage/MainModule/hooks/useForceUpdate';
import {
  ORGANIZATION_DOCUMENTS_BLOCK_ID,
  getOrganizationDocumentBlockId,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmFormOrganizationDocument } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormOrganizationDocument';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef, getKnmIsWatchOnly } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

interface IErknmOrganizationDocumentsProps {
  isEnabled: boolean;
}

interface IErknmOrganizationDocumentsStoreProps {
  formRef: IKnmFormRef | undefined;
  isWatchOnly: boolean;
}

const ErknmOrganizationDocumentsWithoutStore = ({
  isWatchOnly,
  isEnabled,
  formRef,
}: IErknmOrganizationDocumentsProps & IErknmOrganizationDocumentsStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const addField = useCallback(() => {
    if (!isWatchOnly && push && isEnabled) {
      const addingField: IKnmErknmFormOrganizationDocument = {
        value: undefined,
        guid: undefined,
        dateCreate: undefined,
        listId: uuidv4(),
      };

      push('organizationDocuments', addingField);
    }
  }, [isEnabled, push, isWatchOnly]);

  const makeRemoveField = (index: number) => () =>
    !isWatchOnly && isEnabled && remove && remove('organizationDocuments', index);
  const forceUpdate = useForceUpdate();

  useEffectOnce(() => {
    forceUpdate();
  });

  return (
    <FieldBlock
      label={'Перечень представляемых контролируемыми лицами документов'}
      id={ORGANIZATION_DOCUMENTS_BLOCK_ID}
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          type="button"
          variant="link"
          color="primary"
          disabled={isWatchOnly || !isEnabled}
          onClick={addField}
        />
      }
    >
      <FieldArray name="organizationDocuments">
        {({ fields }: FieldArrayRenderProps<IKnmErknmFormOrganizationDocument, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return (
            <Row>
              {fields.map((fieldName, sIndex) => {
                const fieldValue: IKnmErknmFormOrganizationDocument = fields.value?.[sIndex];
                const id = getOrganizationDocumentBlockId(fieldValue.listId);

                return (
                  <Row key={id} margin="medium" isListItem>
                    <TextareaField
                      className="shared-deleting-row"
                      name={`${fieldName}.value`}
                      placeholder="Введите наименование документа"
                      resize="none"
                      disabled={isWatchOnly || !isEnabled}
                    />
                    <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveField(sIndex)} />
                  </Row>
                );
              })}
            </Row>
          );
        }}
      </FieldArray>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IErknmOrganizationDocumentsStoreProps => ({
  formRef: getKnmFormRef(state),
  isWatchOnly: getKnmIsWatchOnly(state),
});

export const ErknmOrganizationDocuments = connect(mapStateToProps)(ErknmOrganizationDocumentsWithoutStore);
